import { Button } from "@components/button";
import { BaseInput, Input } from "@components/input";
import { Modal } from "@components/modal";
import { RowTable } from "@components/row-table";
import { CustomSelect } from "@components/select";
import { Config, DoubleText } from "@components/table";
import { useBoolState } from "@hooks/use-bool-state";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import styles from "./appealModal.module.scss";
import classNames from "classnames";
import {
  AdminAppealResponse,
  AdminAppealUpdateStatusRequest,
  AdminAppealUpdateSumRequest,
  AdminOrderExtended,
} from "@features/admin/appeals/api";
import { formatRubles, formatUsdt } from "@helpers/format";
import { formatDate, formatTime } from "@helpers/date";
import { useAdminAppeals } from "@features/admin/appeals";
import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { statusSchema, sumSchema } from "./schemes";
import { useAuth } from "@features/auth";
import { FlexLoader } from "@components/loader";
import { EllipsisText } from "@components/ellipsis-text";
import { StatusBlock, StatusBlockType } from "@components/status-block";

type CombinedData = AdminAppealResponse &
  AdminOrderExtended & {
    appealCreated: string;
    orderCreated: string;
    // actual_received_amount: number;
    // actual_received_amount_usdt: number;
  };

type Props = {
  isModalOpen: boolean;
  hideModal: () => void;
  data: AdminAppealResponse;
  uuid: string;
};

export const AdminAppealModal = ({
  hideModal,
  isModalOpen,
  data,
  uuid,
}: Props) => {
  const [isSideModalOpen, showSideModal, hideSideModal] = useBoolState(false);
  const { state, getOrderAndAppeals, updateOrderStatus, getAdminAppeals } =
    useAdminAppeals();
  const { control, handleSubmit, reset } =
    useForm<AdminAppealUpdateStatusRequest>({
      resolver: yupResolver(statusSchema),
    });
  const { isAdmin } = useAuth();

  const [sum, setSum] = useState(data.order_start_amount);
  const config: Config<CombinedData> = [
    {
      label: "ID заявки",
      source: "uuid",
      renderComponent: (data) => (
        <EllipsisText text={data} copy startLength={10} />
      ),
    },
    {
      label: "ivnid заявки",
      source: "invid",
      renderComponent: (data) => (
        <EllipsisText text={data} copy startLength={10} />
      ),
    },
    { label: "Email Трейдер", source: "trader_email" },
    { label: "Email Мерч", source: "merchant_email" },
    { label: "Тип заявки", source: "order_type" },
    {
      label: "Курс",
      source: "exchange_rate",
    },
    {
      label: "Сумма заявки",
      source: "order_start_amount",
      renderComponent: (cellData, item) => {
        const usdt = formatUsdt(item.order_start_amount_usdt);
        const rubles = formatRubles(cellData);

        return <DoubleText text={rubles} subText={usdt} />;
      },
    },
    {
      label: "Измененная сумма",
      source: "current_appeal",
      renderComponent: (cellData) => {
        if (
          cellData == null ||
          cellData.merchant_amount_usdt == null ||
          cellData.merchant_amount == null
        )
          return "";
        const usdt = formatUsdt(cellData.merchant_amount_usdt);
        const rubles = formatRubles(cellData.merchant_amount);

        return <DoubleText text={rubles} subText={usdt} />;
      },
      visible: data.order_type == "pay_in",
    },
    {
      label: "Итоговая сумма заявки",
      source: "order_final_amount",
      renderComponent: (cellData, item) => {
        const usdt = formatUsdt(item.order_final_amount_usdt);
        const rubles = formatRubles(cellData);

        return <DoubleText text={rubles} subText={usdt} />;
      },
    },
    {
      label: "Статус заявки",
      source: "order_old_status",
      renderComponent: (status) => {
        let text = "";
        let statusType: StatusBlockType;
        switch (status) {
          case "canceled":
            text = "Отклонена";
            statusType = StatusBlockType.Error;
            break;
          case "completed":
            text = "Успешна";
            statusType = StatusBlockType.Success;
            break;
          case "dispute_open":
            text = "Открыт спор";
            statusType = StatusBlockType.Warning;
            break;
          case "pending":
          case "processing":
          default:
            text = "Ожидает подтверждения";
            statusType = StatusBlockType.Warning;
            break;
        }
        return <StatusBlock text={text} type={statusType} />;
      },
    },
    {
      label: "Статус",
      source: "current_appeal",
      renderComponent: (item) => item.status,
    },
    {
      label: "Дата подачи спора",
      source: "appealCreated",

      renderComponent: (item) => {
        const date = formatDate(new Date(item));
        const time = formatTime(new Date(item));

        return <DoubleText text={time} subText={date} />;
      },
    },
    {
      label: "Дата создания заявки",
      source: "orderCreated",

      renderComponent: (item) => {
        const date = formatDate(new Date(item));
        const time = formatTime(new Date(item));

        return <DoubleText text={time} subText={date} />;
      },
    },
    {
      label: "Решение платформы",
      source: "platform_approve",
      visible: (item) => item != null,
      renderComponent: (item) => {
        if (item == null) return;

        return item ? "Подтверждена" : "Отклонена";
      },
    },
    {
      label: "Время принятия решения платформой",
      source: "platform_decided_at",
      visible: (item) => item != null,
      renderComponent: (item) => {
        if (item == null) return;
        const date = formatDate(new Date(item));
        const time = formatTime(new Date(item));

        return <DoubleText text={time} subText={date} />;
      },
    },
    {
      label: "Сумма из решения платформы",
      source: "platform_decision_amount",
      visible: (item) => item != null,
      renderComponent: (item) => {
        if (item == null) return;
        return formatUsdt(item);
      },
    },
    {
      label: "Причина решения платформы",
      source: "platform_decision_reason",
      visible: (item) => item != null,
    },
  ];
  useEffect(() => {
    getOrderAndAppeals(data.order_uuid);
    reset();
  }, [getOrderAndAppeals, data.order_uuid]);

  const [combined, setCombined] = useState<CombinedData | null>(null);

  useEffect(() => {
    if (state.orderAndAppeals)
      setCombined({
        ...data,
        ...state.orderAndAppeals.order,
        orderCreated: state.orderAndAppeals.order.created_at,
        appealCreated: data.current_appeal.created_at,
        // actual_received_amount: data.current_appeal.actual_received_amount,
        // actual_received_amount_usdt:
        //   data.current_appeal.actual_received_amount_usdt,
      });
    else setCombined(null);
  }, [state.orderAndAppeals, data]);

  const options = [
    { text: "Подтвержден", value: "true" },
    { text: "Отклонен", value: "false" },
  ];

  const onHideSideModal = (newSum?: number) => {
    if (newSum != null) {
      setSum(newSum);
    }
    hideSideModal();
  };

  const onSubmit = (formData: AdminAppealUpdateStatusRequest) => {
    if (state.orderAndAppeals?.appeals) {
      const latestAppeal = state.orderAndAppeals.appeals[0];
      updateOrderStatus(latestAppeal.id, formData).then(() => {
        hideModal();
        getAdminAppeals();
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onClose={hideModal}
        title={" "}
        className={styles.modal}
      >
        {state.loadingOrderAndAppeal ? (
          <FlexLoader style={{ width: "300px", height: "300px" }} />
        ) : (
          <>
            <div className={styles.wrapper}>
              <RowTable
                config={config}
                data={combined}
                className={styles.table}
              />
              <div className={styles.options}>
                <div className={classNames(styles.block, styles.info)}>
                  Файлы мерча:
                  <div className={styles.linksWrapper}>
                    {state.orderAndAppeals?.appeals?.map((appeal, index) => (
                      <div key={index} className={styles.imageLinks}>
                        <span className="font-semibold text-[14px] mb-[5px] mt-[5px]">
                          {formatDate(new Date(appeal.created_at)) +
                            " " +
                            formatTime(new Date(appeal.created_at))}
                        </span>
                        {appeal.check_image_links?.map((check, index) => (
                          <React.Fragment key={index}>
                            <a
                              href={check}
                              target="_blank"
                              className="underline"
                              style={{ textWrap: "nowrap" }}
                            >
                              {check}
                            </a>
                            <br />
                          </React.Fragment>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                <div className={classNames(styles.block, styles.info)}>
                  Сообщение мерча:
                  <div className={styles.linksWrapper}>
                    {state.orderAndAppeals?.appeals?.map((appeal, index) => (
                      <div key={index} className={styles.imageLinks}>
                        <span className="font-semibold text-[14px] mt-[5px]">
                          {formatDate(new Date(appeal.created_at)) +
                            " " +
                            formatTime(new Date(appeal.created_at))}
                        </span>
                        <br />
                        {appeal.message}
                      </div>
                    ))}
                  </div>
                </div>
                <div className={classNames(styles.block, styles.info)}>
                  Файлы трейдера:
                  <div className={styles.linksWrapper}>
                    <div className={styles.imageLinks}>
                      {state.orderAndAppeals?.order?.trader_files?.map(
                        (check, index) => (
                          <React.Fragment key={index}>
                            <a
                              href={check}
                              target="_blank"
                              className="underline"
                              style={{ textWrap: "nowrap" }}
                            >
                              {check}
                            </a>
                            <br />
                          </React.Fragment>
                        )
                      )}
                    </div>
                  </div>
                </div>
                <div className={classNames(styles.block, styles.info)}>
                  Сообщение трейдера:
                  <div className={styles.linksWrapper}>
                    <div className={styles.imageLinks}>
                      {state.orderAndAppeals?.order?.trader_message}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={showSideModal}
                  className={classNames(styles.block, styles.btn)}
                  disabled={!isAdmin}
                >
                  Изменить сумму заявки
                </Button>
                <CustomSelect
                  name="approve"
                  control={control}
                  options={options}
                  classNames={{ warpper: styles.block }}
                  placeholder="Установить статус"
                  disabled={!isAdmin}
                />
              </div>
            </div>
            {state.orderAndAppeals?.order.price_history && (
              <div className={styles.sumArrayWrapper}>
                Массив изменения суммы:
                <div className={styles.sumArray}>
                  {state.orderAndAppeals.order.price_history.map(
                    (item, index) => (
                      <div key={index} className={styles.item}>
                        <div>
                          Время:&nbsp;
                          {formatTime(new Date(item.changed_at)) +
                            " " +
                            formatDate(new Date(item.changed_at))}
                        </div>
                        <div>
                          Сколько:&nbsp;
                          {item.previous_amount}
                        </div>
                        <div>
                          Причина:&nbsp;
                          {item.reason}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={
                !isAdmin /* ||
                data.previous_appeals == null ||
                data.previous_appeals.length == 0 */
              }
            >
              Сохранить
            </Button>
          </>
        )}
      </Modal>

      <SumModal
        isModalOpen={isSideModalOpen}
        hideModal={onHideSideModal}
        data={sum}
        uuid={uuid}
      />
    </>
  );
};

type SumModalProps = {
  isModalOpen: boolean;
  hideModal: (newSum?: number) => void;
  data: number;
  uuid: string;
};

const SumModal = ({ hideModal, isModalOpen, data, uuid }: SumModalProps) => {
  const { state, updateOrderSum } = useAdminAppeals();
  const { control, reset, handleSubmit } = useForm<AdminAppealUpdateSumRequest>(
    {
      defaultValues: {
        new_sum: 0,
        reason: "",
      },
      resolver: yupResolver(sumSchema),
    }
  );

  const onSubmit = (data: AdminAppealUpdateSumRequest) => {
    if (state.orderAndAppeals != null) {
      updateOrderSum(uuid, data).then(() => {
        reset();
      });
      hideModal(data.new_sum);
    }
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={hideModal}
      title={"Изменение суммы заявки"}
    >
      {state.isSumModalLoading ? (
        <FlexLoader style={{ width: "200px", height: "200px" }} />
      ) : (
        <form className={styles.sumModal}>
          <BaseInput
            className={styles.item}
            disabled
            placeholder={String(data)}
            label="Старая сумма"
            value={state.orderAndAppeals?.order.order_start_amount ?? 0}
          ></BaseInput>
          <Input
            className={styles.item}
            control={control}
            name="new_sum"
            type="number"
            label="Новая сумма"
          ></Input>
          <Input
            className={styles.item}
            control={control}
            name="reason"
            label="Причина"
          ></Input>
          <Button onClick={handleSubmit(onSubmit)}>Сохранить</Button>
        </form>
      )}
    </Modal>
  );
};
